
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Head from 'next/head';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { FaApple, FaArrowRight, FaGooglePlay } from 'react-icons/fa6';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import appIcon from '../../apps/mobile/google-play/assets/app-icon-512.png';
import markyScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/01-marky-ai-agent.png';
import imageScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/02-ai-image-generator.png';
import videoScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/03-ai-video-generator.png';
import transcriptionScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/04-ai-transcription.png';
import audioScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/05-ai-voice-and-music.png';
import toolsScreen from '../../apps/mobile/google-play/assets/phone-screenshots/en-US/06-all-ai-tools.png';
const APP_STORE_URL = 'https://apps.apple.com/us/app/easy-peasy-ai/id6805346363';
const screenshots = [
    {
        src: markyScreen,
        altKey: 'screenshot_marky_alt',
    },
    {
        src: imageScreen,
        altKey: 'screenshot_image_alt',
    },
    {
        src: videoScreen,
        altKey: 'screenshot_video_alt',
    },
    {
        src: transcriptionScreen,
        altKey: 'screenshot_transcription_alt',
    },
    {
        src: audioScreen,
        altKey: 'screenshot_audio_alt',
    },
    {
        src: toolsScreen,
        altKey: 'screenshot_tools_alt',
    },
];
function AppStoreButton({ compact = false }: {
    compact?: boolean;
}) {
    const { t } = useLocale('mobile-app');
    return (<a href={APP_STORE_URL} target='_blank' rel='noopener noreferrer' className={`group inline-flex items-center justify-center gap-3 bg-[#17131f] text-[#fffaf3] transition duration-300 hover:-translate-y-1 hover:bg-[#2a2138] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7657ff]/35 ${compact
            ? 'rounded-xl px-5 py-3 shadow-[4px_4px_0_#7657ff]'
            : 'rounded-2xl px-6 py-4 shadow-[6px_6px_0_#7657ff]'}`} aria-label={t('app_store_aria')}>
      <FaApple className={compact ? 'h-6 w-6' : 'h-8 w-8'}/>
      <span className='text-start leading-none'>
        <span className='block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#dcd2ed]'>
          {t('app_store_download')}
        </span>
        <span className={`mt-1 block font-bold ${compact ? 'text-base' : 'text-xl'}`}>
          App Store
        </span>
      </span>
      {!compact && (<FaArrowRight className='ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1'/>)}
    </a>);
}
function ScreenshotFrame({ src, alt, className = '', priority = false, }: {
    src: (typeof screenshots)[number]['src'];
    alt: string;
    className?: string;
    priority?: boolean;
}) {
    return (<div className={`relative overflow-hidden rounded-[2rem] border-[3px] border-[#17131f] bg-[#17131f] shadow-[10px_12px_0_#7657ff] ${className}`}>
      <Image src={src} alt={alt} className='h-full w-full object-cover' sizes='(max-width: 768px) 72vw, 360px' priority={priority}/>
    </div>);
}
export default function MobileAppLandingPage() {
    const { t, lang } = useLocale('mobile-app');
    const workflow = [
        {
            number: '01',
            title: t('workflow_ask_title'),
            body: t('workflow_ask_body'),
        },
        {
            number: '02',
            title: t('workflow_create_title'),
            body: t('workflow_create_body'),
        },
        {
            number: '03',
            title: t('workflow_move_title'),
            body: t('workflow_move_body'),
        },
    ];
    const faqs = [
        {
            question: t('faq_free_question'),
            answer: t('faq_free_answer'),
        },
        {
            question: t('faq_account_question'),
            answer: t('faq_account_answer'),
        },
        {
            question: t('faq_android_question'),
            answer: t('faq_android_answer'),
        },
        {
            question: t('faq_marky_question'),
            answer: t('faq_marky_answer'),
        },
    ];
    return (<main dir={lang === 'ar' ? 'rtl' : 'ltr'} className='min-h-screen overflow-hidden bg-[#f8f3ff] text-[#17131f]'>
      <Seo templateTitle={t('seo_title')} description={t('seo_description')} image='https://media.easy-peasy.ai/landing/home/og-home-v3.jpg' page='product' productName={t('seo_product_name')} productDescription={t('seo_product_description')}/>
      <Head>
        <meta name='apple-itunes-app' content='app-id=6805346363'/>
        <meta name='theme-color' content='#100c18'/>
      </Head>

      <section className='relative isolate overflow-hidden bg-[#100c18] text-[#fffaf3]'>
        <div className='absolute inset-x-0 top-0 z-40'>
          <LandingPageNavigation variant='light' skipUserContext forceDark/>
        </div>

        <div aria-hidden='true' className='absolute -left-24 top-40 h-72 w-72 rounded-full bg-[#ff8f7d]/25 blur-[90px]'/>
        <div aria-hidden='true' className='absolute -right-12 top-24 h-[34rem] w-[34rem] rounded-full bg-[#7657ff]/25 blur-[110px]'/>
        <div aria-hidden='true' className='absolute inset-0 opacity-[0.09] [background-image:linear-gradient(rgba(255,255,255,.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.2)_1px,transparent_1px)] [background-size:48px_48px]'/>

        <div className='relative mx-auto grid min-h-[860px] max-w-7xl items-center gap-16 px-5 pb-24 pt-36 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8 lg:px-10 lg:pb-20 lg:pt-32'>
          <div className='hero-copy relative z-20 max-w-2xl'>
            <div className='mb-7 inline-flex items-center gap-3 rounded-full border border-[#fffaf3]/20 bg-[#fffaf3]/5 py-2 pl-2 pr-4 text-xs font-bold uppercase tracking-[0.18em] text-[#eee4fb] backdrop-blur'>
              <Image src={appIcon} alt={t('app_icon_alt')} className='h-9 w-9 rounded-full' priority/>
              {t('hero_eyebrow')}
            </div>

            <h1 className='max-w-3xl text-[clamp(3.5rem,8vw,7.6rem)] font-black leading-[0.86] tracking-[-0.07em]'>
              {t('hero_title')}
              <span className='mt-3 block text-[#c9b8ff]'>
                {t('hero_title_accent')}
              </span>
            </h1>

            <p className='mt-8 max-w-xl text-lg leading-8 text-[#dcd2e9] sm:text-xl'>
              {t('hero_description')}
            </p>

            <div className='mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center'>
              <AppStoreButton />
              <div className='inline-flex items-center gap-3 rounded-2xl border border-[#fffaf3]/15 px-5 py-3 text-start text-sm text-[#dcd2e9]'>
                <FaGooglePlay className='h-6 w-6 text-[#ff9d8d]'/>
                <span>
                  <span className='block font-bold text-[#fffaf3]'>
                    {t('android_label')}
                  </span>
                  {t('android_status')}
                </span>
              </div>
            </div>

            <p className='mt-7 text-sm font-medium text-[#bcb0ca]'>
              {t('availability_note')}
            </p>
          </div>

          <div className='hero-visual relative mx-auto h-[510px] w-full max-w-[620px] sm:h-[650px] lg:h-[720px]'>
            <div className='phone-left absolute left-[1%] top-[20%] z-0 w-[42%] -rotate-[11deg] opacity-75 sm:left-[3%] sm:top-[16%]'>
              <ScreenshotFrame src={audioScreen} alt={t('screenshot_audio_alt')} className='shadow-[8px_9px_0_#ff8f7d]'/>
            </div>
            <div className='phone-right absolute right-[0%] top-[16%] z-10 w-[43%] rotate-[10deg] opacity-85 sm:right-[1%] sm:top-[12%]'>
              <ScreenshotFrame src={videoScreen} alt={t('screenshot_video_alt')} className='shadow-[8px_9px_0_#ffe1a8]'/>
            </div>
            <div className='phone-center absolute left-1/2 top-[7%] z-20 w-[51%] -translate-x-1/2 sm:top-[3%]'>
              <ScreenshotFrame src={markyScreen} alt={t('screenshot_marky_short_alt')} priority/>
            </div>
            <div className='absolute bottom-1 left-1/2 z-30 -translate-x-1/2 rotate-[-2deg] border-2 border-[#17131f] bg-[#fffaf3] px-5 py-3 text-sm font-black uppercase tracking-[0.14em] text-[#17131f] shadow-[6px_6px_0_#ff8f7d] sm:text-base'>
              {t('hero_badge')}
            </div>
          </div>
        </div>
      </section>

      <section className='border-b-2 border-[#17131f] bg-[#ffdbd2]'>
        <div className='mx-auto grid max-w-7xl grid-cols-1 divide-y-2 divide-[#17131f] px-5 sm:grid-cols-3 sm:divide-x-2 sm:divide-y-0 sm:px-8 lg:px-10'>
          {[
            [t('stat_available_label'), 'App Store'],
            [t('stat_built_label'), 'iPhone'],
            [t('stat_login_label'), t('stat_login_value')],
        ].map(([label, value]) => (<div key={label} className='py-7 sm:px-8 sm:py-8'>
              <div className='text-xs font-black uppercase tracking-[0.18em] text-[#7b4d50]'>
                {label}
              </div>
              <div className='mt-2 text-2xl font-black tracking-tight'>
                {value}
              </div>
            </div>))}
        </div>
      </section>

      <section className='px-5 py-24 sm:px-8 sm:py-32 lg:px-10'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid items-end gap-8 lg:grid-cols-[0.7fr_1.3fr]'>
            <p className='text-xs font-black uppercase tracking-[0.22em] text-[#6748d9]'>
              {t('momentum_eyebrow')}
            </p>
            <h2 className='max-w-4xl text-[clamp(2.8rem,6vw,5.8rem)] font-black leading-[0.94] tracking-[-0.055em]'>
              {t('momentum_title')}
            </h2>
          </div>

          <article className='mt-20 grid items-center gap-12 border-y-2 border-[#17131f] py-16 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20 lg:py-24'>
            <div className='mx-auto w-full max-w-[400px] lg:order-2'>
              <ScreenshotFrame src={markyScreen} alt={t('screenshot_marky_result_alt')} className='rotate-[2deg] shadow-[12px_14px_0_#ff8f7d]'/>
            </div>
            <div>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6748d9]'>
                {t('marky_eyebrow')}
              </p>
              <h3 className='mt-5 max-w-xl text-5xl font-black leading-[0.95] tracking-[-0.05em] sm:text-6xl'>
                {t('marky_title')}
              </h3>
              <p className='mt-7 max-w-xl text-lg leading-8 text-[#51475f]'>
                {t('marky_description')}
              </p>
              <div className='mt-10 flex flex-wrap gap-3'>
                {[
            t('marky_tag_research'),
            t('marky_tag_presentations'),
            t('marky_tag_websites'),
            t('marky_tag_documents'),
        ].map((item) => (<span key={item} className='border-2 border-[#17131f] bg-[#fffaf3] px-4 py-2 text-sm font-bold shadow-[3px_3px_0_#c9b8ff]'>
                    {item}
                  </span>))}
              </div>
            </div>
          </article>

          <div className='grid border-b-2 border-[#17131f] lg:grid-cols-2'>
            <article className='border-b-2 border-[#17131f] py-16 lg:border-b-0 lg:border-r-2 lg:py-20 lg:pr-16'>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6748d9]'>
                {t('creative_eyebrow')}
              </p>
              <h3 className='mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl'>
                {t('creative_title')}
              </h3>
              <p className='mt-5 max-w-lg text-lg leading-8 text-[#51475f]'>
                {t('creative_description')}
              </p>
              <div className='mt-12 grid grid-cols-2 gap-4 pr-3 sm:gap-6'>
                <div className='rotate-[-3deg]'>
                  <ScreenshotFrame src={imageScreen} alt={t('screenshot_image_alt')} className='rounded-[1.5rem] shadow-[6px_7px_0_#ff8f7d]'/>
                </div>
                <div className='translate-y-9 rotate-[3deg]'>
                  <ScreenshotFrame src={videoScreen} alt={t('screenshot_video_alt')} className='rounded-[1.5rem] shadow-[6px_7px_0_#ffe1a8]'/>
                </div>
              </div>
            </article>

            <article className='py-16 lg:py-20 lg:pl-16'>
              <p className='text-sm font-black uppercase tracking-[0.2em] text-[#6748d9]'>
                {t('audio_eyebrow')}
              </p>
              <h3 className='mt-4 text-4xl font-black tracking-[-0.045em] sm:text-5xl'>
                {t('audio_title')}
              </h3>
              <p className='mt-5 max-w-lg text-lg leading-8 text-[#51475f]'>
                {t('audio_description')}
              </p>
              <div className='mt-12 grid grid-cols-2 gap-4 pr-3 sm:gap-6'>
                <div className='translate-y-9 rotate-[-3deg]'>
                  <ScreenshotFrame src={transcriptionScreen} alt={t('screenshot_transcription_alt')} className='rounded-[1.5rem] shadow-[6px_7px_0_#ffe1a8]'/>
                </div>
                <div className='rotate-[3deg]'>
                  <ScreenshotFrame src={audioScreen} alt={t('screenshot_audio_alt')} className='rounded-[1.5rem] shadow-[6px_7px_0_#ff8f7d]'/>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='bg-[#17131f] px-5 py-24 text-[#fffaf3] sm:px-8 sm:py-32 lg:px-10'>
        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-end'>
            <div>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#c9b8ff]'>
                {t('workflow_eyebrow')}
              </p>
              <h2 className='mt-5 max-w-2xl text-[clamp(3rem,6vw,5.8rem)] font-black leading-[0.92] tracking-[-0.055em]'>
                {t('workflow_title')}
              </h2>
            </div>
            <p className='max-w-xl text-lg leading-8 text-[#cfc4dd] lg:justify-self-end'>
              {t('workflow_description')}
            </p>
          </div>

          <ol className='mt-20 grid border-y border-[#fffaf3]/25 lg:grid-cols-3 lg:divide-x lg:divide-[#fffaf3]/25'>
            {workflow.map((step) => (<li key={step.number} className='border-b border-[#fffaf3]/25 py-10 last:border-b-0 lg:border-b-0 lg:px-10 lg:py-14 first:lg:pl-0 last:lg:pr-0'>
                <div className='text-sm font-black text-[#ff9d8d]'>
                  {step.number}
                </div>
                <h3 className='mt-10 text-4xl font-black tracking-[-0.04em]'>
                  {step.title}
                </h3>
                <p className='mt-4 max-w-sm text-base leading-7 text-[#cfc4dd]'>
                  {step.body}
                </p>
              </li>))}
          </ol>
        </div>
      </section>

      <section className='bg-[#fffaf3] py-24 sm:py-32'>
        <div className='px-5 sm:px-8 lg:px-10'>
          <div className='mx-auto flex max-w-7xl flex-col justify-between gap-7 md:flex-row md:items-end'>
            <div>
              <p className='text-xs font-black uppercase tracking-[0.22em] text-[#6748d9]'>
                {t('gallery_eyebrow')}
              </p>
              <h2 className='mt-4 max-w-3xl text-[clamp(2.8rem,6vw,5.5rem)] font-black leading-[0.94] tracking-[-0.055em]'>
                {t('gallery_title')}
              </h2>
            </div>
            <p className='max-w-sm text-lg leading-8 text-[#5b5066]'>
              {t('gallery_description')}
            </p>
          </div>
        </div>

        <div className='mt-16 overflow-x-auto px-5 pb-8 sm:px-8 lg:px-10'>
          <div className='mx-auto flex w-max gap-6'>
            {screenshots.map((screenshot, index) => (<figure key={screenshot.altKey} className={`w-[250px] shrink-0 sm:w-[300px] ${index % 2 === 0
                ? '-rotate-[1.5deg]'
                : 'translate-y-8 rotate-[1.5deg]'}`}>
                <ScreenshotFrame src={screenshot.src} alt={t(screenshot.altKey)} className='rounded-[1.5rem] shadow-[7px_8px_0_#7657ff]'/>
              </figure>))}
          </div>
        </div>
      </section>

      <section className='border-y-2 border-[#17131f] bg-[#dcd0ff] px-5 py-24 sm:px-8 sm:py-32 lg:px-10'>
        <div className='mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.7fr_1.3fr]'>
          <div>
            <p className='text-xs font-black uppercase tracking-[0.22em] text-[#6748d9]'>
              {t('faq_eyebrow')}
            </p>
            <h2 className='mt-4 text-5xl font-black tracking-[-0.05em] sm:text-6xl'>
              {t('faq_title')}
            </h2>
          </div>

          <div className='divide-y-2 divide-[#17131f] border-y-2 border-[#17131f]'>
            {faqs.map((faq) => (<details key={faq.question} className='group'>
                <summary className='flex cursor-pointer list-none items-center justify-between gap-8 py-6 text-xl font-black marker:content-none sm:text-2xl'>
                  {faq.question}
                  <span aria-hidden='true' className='text-3xl font-normal transition-transform duration-300 group-open:rotate-45'>
                    +
                  </span>
                </summary>
                <p className='max-w-2xl pb-7 pr-12 text-base leading-7 text-[#51475f] sm:text-lg sm:leading-8'>
                  {faq.answer}
                </p>
              </details>))}
          </div>
        </div>
      </section>

      <section className='relative overflow-hidden bg-[#ff8f7d] px-5 py-24 sm:px-8 sm:py-32 lg:px-10'>
        <div aria-hidden='true' className='absolute -right-24 -top-24 h-80 w-80 rounded-full border-[48px] border-[#fffaf3]/35'/>
        <div className='relative mx-auto grid max-w-7xl items-end gap-12 lg:grid-cols-[1.2fr_0.8fr]'>
          <div>
            <p className='text-xs font-black uppercase tracking-[0.22em] text-[#633239]'>
              {t('final_eyebrow')}
            </p>
            <h2 className='mt-5 max-w-4xl text-[clamp(3.4rem,8vw,7rem)] font-black leading-[0.88] tracking-[-0.065em]'>
              {t('final_title')}
            </h2>
          </div>
          <div className='lg:justify-self-end'>
            <AppStoreButton />
            <p className='mt-6 max-w-sm text-sm font-semibold leading-6 text-[#633239]'>
              {t('final_description')}
            </p>
          </div>
        </div>
      </section>

      <div className='dark'>
        <Footer variant='dark'/>
      </div>

      <div className='fixed inset-x-0 bottom-0 z-50 border-t-2 border-[#17131f] bg-[#fffaf3]/95 px-4 py-3 backdrop-blur md:hidden'>
        <div className='mx-auto flex max-w-md items-center justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-3'>
            <Image src={appIcon} alt='' className='h-11 w-11 shrink-0 rounded-xl'/>
            <div className='min-w-0'>
              <div className='truncate text-sm font-black'>Easy-Peasy.AI</div>
              <div className='text-xs text-[#655a70]'>
                {t('mobile_sticky_note')}
              </div>
            </div>
          </div>
          <AppStoreButton compact/>
        </div>
      </div>

      <style jsx>{`
        .hero-copy {
          animation: app-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .hero-visual {
          animation: app-rise 900ms 120ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .phone-center {
          animation: phone-breathe 8s 1.1s ease-in-out infinite;
        }
        .phone-left {
          animation: phone-left 9s 1.2s ease-in-out infinite;
        }
        .phone-right {
          animation: phone-right 10s 1.3s ease-in-out infinite;
        }
        @keyframes app-rise {
          from {
            opacity: 0;
            transform: translateY(32px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes phone-breathe {
          0%,
          100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-10px);
          }
        }
        @keyframes phone-left {
          0%,
          100% {
            transform: rotate(-11deg) translateY(0);
          }
          50% {
            transform: rotate(-9deg) translateY(-8px);
          }
        }
        @keyframes phone-right {
          0%,
          100% {
            transform: rotate(10deg) translateY(0);
          }
          50% {
            transform: rotate(8deg) translateY(-12px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-copy,
          .hero-visual,
          .phone-center,
          .phone-left,
          .phone-right {
            animation: none;
          }
        }
      `}</style>
    </main>);
}
async function getStaticProps() {
    return {
        props: {
            isStaticPage: true,
        },
    };
}

    async function __Next_Translate__getStaticProps__1a15314eaea__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/mobile-app',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a15314eaea__ as getStaticProps }
  